import React from 'react';
import { Home, PenTool, HardHat, ArrowRight, MapPin, Clock, Phone, Award, Shield, ThumbsUp, Users, Calendar } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
export function Audiences() {
  const audiences = [{
    title: 'Homeowners',
    copy: 'Get help choosing the right glass, hardware and enclosure style for your bathroom and budget.',
    icon: Home
  }, {
    title: 'Bathroom Remodelers & Designers',
    copy: 'Coordinate shower glass options, final measurements and installation details for a polished finished bathroom.',
    icon: PenTool
  }, {
    title: 'Builders & Contractors',
    copy: 'Bring Kosin’s Glass into the process for custom shower glass on new homes, remodels, additions and renovation projects.',
    icon: HardHat
  }];
  return <section className="py-20 bg-navy text-white" data-id="element-17">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-18">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-19">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-20">
            For Homeowners, Bathroom Remodelers, Designers & Builders
          </h2>
          <p className="text-lg text-blue-100" data-id="element-21">
            Kosin’s Glass works with homeowners planning bathroom upgrades as
            well as remodelers, designers, builders and contractors who need
            custom shower glass measured, fabricated and installed correctly.
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-22">
          {audiences.map((aud, i) => <FadeIn key={i} delay={i * 0.1} className="bg-navy-800 p-8 rounded-2xl border border-navy-700" data-id="element-23">
              <aud.icon className="w-10 h-10 text-primary mb-6" data-id="element-24" />
              <h3 className="text-xl font-bold mb-3" data-id="element-25">{aud.title}</h3>
              <p className="text-blue-100 leading-relaxed" data-id="element-26">{aud.copy}</p>
            </FadeIn>)}
        </div>

        <FadeIn className="text-center" data-id="element-27">
          <a href="#quote-form" className="inline-flex items-center justify-center bg-primary hover:bg-primary-hover text-white font-semibold py-4 px-8 rounded-xl transition-colors" data-id="element-28">
            Talk to a Shower Glass Specialist
          </a>
        </FadeIn>
      </div>
    </section>;
}
export function RelatedServices() {
  const services = [{
    title: 'Residential Glass',
    copy: 'Broken windows, door glass, custom glass and other residential glass services.',
    link: '/residentialglass.html'
  }, {
    title: 'Window Glass',
    copy: 'Glass-only window replacement, foggy double-pane glass and broken window glass.',
    link: '/windowglass.html'
  }, {
    title: 'Custom Mirrors',
    copy: 'Bathroom mirrors, vanity mirrors, wall mirrors and home gym mirrors.',
    link: '/customglassmirrors.html'
  }, {
    title: 'Glass Deck Railing',
    copy: 'Custom glass railing panels for decks and outdoor views.',
    link: '/deckglass.html'
  }];
  return <section className="py-16 bg-gray-50 border-b border-gray-200" data-id="element-29">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-30">
        <FadeIn data-id="element-31">
          <h2 className="text-2xl font-serif font-bold text-navy mb-8 text-center" data-id="element-32">
            More Custom Glass for Your Home
          </h2>
        </FadeIn>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-33">
          {services.map((svc, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-34">
              <a href={svc.link} className="block bg-white p-5 rounded-xl border border-gray-100 hover:border-primary hover:shadow-md transition-all group h-full" data-id="element-35">
                <h3 className="font-bold text-navy mb-2 group-hover:text-primary transition-colors flex items-center justify-between" data-id="element-36">
                  {svc.title}
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0" data-id="element-37" />
                </h3>
                <p className="text-sm text-gray-500" data-id="element-38">{svc.copy}</p>
              </a>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}
export function WhyChoose() {
  const reasons = [{
    title: 'Local Michigan Glass Experts',
    copy: 'Family-owned since 2000 and serving homeowners throughout Howell, Ann Arbor, Novi and Southeast Michigan.',
    icon: MapPin
  }, {
    title: 'Custom Design & Measurement',
    copy: 'Kosin’s Glass reviews your bathroom, takes proper measurements and helps design the right shower glass solution.',
    icon: PenTool
  }, {
    title: 'Professional Installation',
    copy: 'Experienced glass technicians install custom shower doors and enclosures with attention to fit, safety and finish.',
    icon: Award
  }, {
    title: 'Quality Glass & Hardware',
    copy: 'Choose from quality glass, trusted shower lines, 18 glass designs and textures, and many hardware finishes.',
    icon: Shield
  }, {
    title: 'No High-Pressure Sales',
    copy: 'Get clear recommendations, options and a free in-home quote before making a decision.',
    icon: ThumbsUp
  }, {
    title: 'Lifetime Installation Guarantee',
    copy: 'The current Kosin’s page states that shower door installations are backed by a lifetime guarantee.',
    icon: Shield
  }];
  return <section className="py-20 bg-white" data-id="element-39">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-40">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-41">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-42">
            Why Choose Kosin’s Glass for Shower Doors?
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-43">
          {reasons.map((reason, i) => <FadeIn key={i} delay={i * 0.1} className="bg-gray-50 p-8 rounded-2xl border border-gray-100 flex gap-4" data-id="element-44">
              <div className="shrink-0 mt-1" data-id="element-45">
                <reason.icon className="w-6 h-6 text-primary" data-id="element-46" />
              </div>
              <div data-id="element-47">
                <h3 className="text-lg font-bold text-navy mb-2" data-id="element-48">
                  {reason.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-49">
                  {reason.copy}
                </p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}
export function Locations() {
  const locations = [{
    name: 'Howell Showroom & Main Service Center',
    address: '222 W Grand River Ave, Howell, MI 48843',
    phone: '517-545-7772',
    phoneLink: '5175457772'
  }, {
    name: 'Ann Arbor Satellite Office',
    address: '2723 South State St, Suite 150, Ann Arbor, MI 48104',
    phone: '734-887-6556',
    phoneLink: '7348876556'
  }, {
    name: 'Novi Satellite Office',
    address: '39555 Orchard Hill Place, Suite 600, Novi, MI 48375',
    phone: '248-924-9899',
    phoneLink: '2489249899'
  }];
  return <section className="py-20 bg-glass" data-id="element-50">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-51">
        <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-52">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-53">
            Glass Shower Doors Near Howell, Ann Arbor, Novi & Southeast Michigan
          </h2>
          <p className="text-lg text-gray-600" data-id="element-54">
            Kosin’s Glass installs custom glass shower doors and enclosures
            across Southeast Michigan, including Howell, Ann Arbor, Novi,
            Brighton, Fenton, Grand Blanc, Hartland, Fowlerville, Northville,
            Lansing, East Lansing, Webberville, Williamston and nearby
            communities.
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-55">
          {locations.map((loc, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white p-8 rounded-2xl shadow-sm border border-blue-50 text-center" data-id="element-56">
              <MapPin className="w-8 h-8 text-primary mx-auto mb-4" data-id="element-57" />
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-58">{loc.name}</h3>
              <p className="text-gray-600 mb-4" data-id="element-59">{loc.address}</p>
              <a href={`tel:${loc.phoneLink}`} className="inline-flex items-center gap-2 text-primary font-bold hover:text-primary-hover transition-colors" data-id="element-60">
                <Phone className="w-4 h-4" data-id="element-61" />
                {loc.phone}
              </a>
            </FadeIn>)}
        </div>

        <FadeIn delay={0.3} className="max-w-md mx-auto bg-white p-6 rounded-2xl shadow-sm border border-blue-50 flex items-start gap-4" data-id="element-62">
          <Clock className="w-6 h-6 text-primary shrink-0 mt-1" data-id="element-63" />
          <div data-id="element-64">
            <h4 className="font-bold text-navy mb-2" data-id="element-65">Hours of Operation</h4>
            <ul className="text-gray-600 space-y-1 text-sm" data-id="element-66">
              <li className="flex justify-between" data-id="element-67">
                <span data-id="element-68">Monday–Friday:</span> <span data-id="element-69">8:00 a.m. to 5:00 p.m.</span>
              </li>
              <li className="flex justify-between" data-id="element-70">
                <span data-id="element-71">Saturday:</span> <span data-id="element-72">9:00 a.m. to 3:00 p.m.</span>
              </li>
              <li className="flex justify-between" data-id="element-73">
                <span data-id="element-74">Sunday:</span> <span data-id="element-75">Closed</span>
              </li>
            </ul>
          </div>
        </FadeIn>
      </div>
    </section>;
}